<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import InboxReconnectionRequired from '../../components/InboxReconnectionRequired.vue';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import { redirectToCanonicalMetaHost } from 'dashboard/helper/canonicalMetaAuth';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  whatsappRegistrationIncomplete: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const { prepareEmbeddedSignup, runEmbeddedSignup } =
  useWhatsappEmbeddedSignup();
let unmounted = false;
onBeforeUnmount(() => {
  unmounted = true;
});

const isRequestingAuthorization = ref(false);
const isLoadingFacebook = ref(true);
const registrationDialogRef = ref(null);
const phoneAlreadyVerified = ref(false);
const verificationCodeRequested = ref(false);
const verificationCode = ref('');
const verificationPin = ref('');
const verificationCodeMethod = ref('SMS');
const REGISTRATION_POLL_INTERVAL_MS = 1500;
const REGISTRATION_POLL_ATTEMPTS = 20;
const verificationPinIsValid = computed(() =>
  /^\d{6}$/.test(verificationPin.value)
);
const verificationCodeIsValid = computed(
  () => phoneAlreadyVerified.value || /^\d{6}$/.test(verificationCode.value)
);
const verificationActionLabel = computed(() =>
  phoneAlreadyVerified.value
    ? t('INBOX_MGMT.WHATSAPP_REGISTER_WITH_PIN')
    : t('INBOX_MGMT.WHATSAPP_VERIFY_AND_REGISTER')
);

const whatsappAppId = computed(() => window.lumeratecConfig.whatsappAppId);
const whatsappConfigurationId = computed(
  () => window.lumeratecConfig.whatsappConfigurationId
);

const actionLabel = computed(() => {
  if (props.whatsappRegistrationIncomplete) {
    return t('INBOX_MGMT.WHATSAPP_OPEN_REGISTRATION_DIALOG');
  }
  return '';
});

const description = computed(() => {
  if (props.whatsappRegistrationIncomplete) {
    return t('INBOX_MGMT.WHATSAPP_REGISTRATION_INCOMPLETE');
  }
  return '';
});

const wait = milliseconds =>
  new Promise(resolve => {
    window.setTimeout(resolve, milliseconds);
  });

const waitForSetupCompletion = async attempt => {
  if (unmounted) return false;
  if (attempt >= REGISTRATION_POLL_ATTEMPTS) {
    useAlert(t('INBOX_MGMT.WHATSAPP_SETUP_PENDING'));
    return false;
  }

  await wait(REGISTRATION_POLL_INTERVAL_MS);
  if (unmounted) return false;

  try {
    await store.dispatch('inboxes/get');
    const response = await InboxHealthAPI.getHealthStatus(props.inbox.id);
    const health = response.data;

    if (
      health.setup_status === 'failed' ||
      health.setup_status === 'registration_required'
    ) {
      useAlert(
        health.setup_error || t('INBOX_MGMT.WHATSAPP_REGISTRATION_INCOMPLETE')
      );
      return false;
    }
    if (health.setup_status === 'complete' && health.sending_enabled === true)
      return true;
  } catch {
    // A temporary health error is not confirmation of successful registration.
  }

  return waitForSetupCompletion(attempt + 1);
};

const reauthorizeWhatsApp = async params => {
  isRequestingAuthorization.value = true;

  try {
    const response = await whatsappChannel.reauthorizeWhatsApp({
      inboxId: props.inbox.id,
      ...params,
    });

    if (response.data.success) {
      useAlert(t('INBOX_MGMT.WHATSAPP_SETUP_PENDING'));

      const completed = await waitForSetupCompletion(0);
      if (completed) {
        useAlert(t('INBOX.REAUTHORIZE.SUCCESS'));
      }
    } else {
      useAlert(response.data.message || t('INBOX.REAUTHORIZE.ERROR'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INBOX.REAUTHORIZE.ERROR')
    );
  } finally {
    isRequestingAuthorization.value = false;
  }
};

const requestPhoneVerificationCode = async (codeMethod = 'SMS') => {
  if (isRequestingAuthorization.value) return;
  verificationCodeMethod.value = codeMethod;
  isRequestingAuthorization.value = true;
  try {
    const response = await whatsappChannel.requestPhoneVerificationCode({
      inboxId: props.inbox.id,
      codeMethod,
    });

    if (response.data.success) {
      phoneAlreadyVerified.value = response.data.already_verified === true;
      verificationCodeRequested.value = !phoneAlreadyVerified.value;
      useAlert(
        response.data.message || t('INBOX_MGMT.WHATSAPP_VERIFICATION_CODE_SENT')
      );
    } else {
      useAlert(response.data.message || t('INBOX.REAUTHORIZE.ERROR'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INBOX.REAUTHORIZE.ERROR')
    );
  } finally {
    isRequestingAuthorization.value = false;
  }
};

const clearRegistrationSecrets = () => {
  verificationCode.value = '';
  verificationPin.value = '';
};

const openRegistrationDialog = () => {
  clearRegistrationSecrets();
  registrationDialogRef.value?.open();
};

const closeRegistrationDialog = () => {
  clearRegistrationSecrets();
  registrationDialogRef.value?.close();
};

const verifyPhoneRegistration = async () => {
  if (isRequestingAuthorization.value) return;
  if (!verificationCodeIsValid.value) {
    useAlert(t('INBOX_MGMT.WHATSAPP_VERIFICATION_CODE_INVALID'));
    return;
  }
  if (!verificationPinIsValid.value) {
    useAlert(t('INBOX_MGMT.WHATSAPP_VERIFICATION_PIN_INVALID'));
    return;
  }

  isRequestingAuthorization.value = true;
  try {
    const response = await whatsappChannel.verifyPhoneRegistration({
      inboxId: props.inbox.id,
      verificationCode: verificationCode.value,
      verificationPin: verificationPin.value,
    });

    if (response.data.success) {
      verificationPin.value = '';
      useAlert(t('INBOX_MGMT.WHATSAPP_REGISTRATION_RETRY_STARTED'));
      const completed = await waitForSetupCompletion(0);
      if (completed) {
        useAlert(t('INBOX.REAUTHORIZE.SUCCESS'));
        closeRegistrationDialog();
      }
    } else {
      useAlert(response.data.message || t('INBOX.REAUTHORIZE.ERROR'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INBOX.REAUTHORIZE.ERROR')
    );
  } finally {
    isRequestingAuthorization.value = false;
  }
};

const requestAuthorization = async () => {
  if (isRequestingAuthorization.value) return;
  if (props.whatsappRegistrationIncomplete) {
    openRegistrationDialog();
    return;
  }
  if (isLoadingFacebook.value) {
    useAlert(t('INBOX.REAUTHORIZE.LOADING_FACEBOOK'));
    return;
  }

  isRequestingAuthorization.value = true;
  try {
    const credentials = await runEmbeddedSignup();
    if (credentials) await reauthorizeWhatsApp(credentials);
  } catch (error) {
    useAlert(error.message || t('INBOX.REAUTHORIZE.CONFIGURATION_ERROR'));
  } finally {
    isRequestingAuthorization.value = false;
  }
};

onMounted(async () => {
  try {
    if (await redirectToCanonicalMetaHost()) return;

    // Validate required configuration
    if (!whatsappAppId.value) {
      useAlert(t('INBOX.REAUTHORIZE.WHATSAPP_APP_ID_MISSING'));
      return;
    }
    if (!whatsappConfigurationId.value) {
      useAlert(t('INBOX.REAUTHORIZE.WHATSAPP_CONFIG_ID_MISSING'));
      return;
    }

    // Load Facebook SDK and initialize
    await prepareEmbeddedSignup();
  } catch (error) {
    useAlert(t('INBOX.REAUTHORIZE.FACEBOOK_LOAD_ERROR'));
  } finally {
    isLoadingFacebook.value = false;
  }
});

// Expose requestAuthorization function for parent components
defineExpose({
  requestAuthorization,
});
</script>

<template>
  <div class="mx-6">
    <InboxReconnectionRequired
      :is-loading="isRequestingAuthorization"
      :action-label="actionLabel"
      :description="description"
      @reauthorize="requestAuthorization"
    />

    <Dialog
      ref="registrationDialogRef"
      width="xl"
      :title="t('INBOX_MGMT.WHATSAPP_REGISTRATION_DIALOG_TITLE')"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="clearRegistrationSecrets"
    >
      <div class="flex flex-col gap-4">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.WHATSAPP_VERIFICATION_CODE_HELP') }}
        </p>

        <div v-if="!verificationCodeRequested && !phoneAlreadyVerified">
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_VERIFICATION_METHOD_LABEL') }}
          </p>
          <div class="flex flex-wrap gap-3">
            <NextButton
              :is-loading="
                isRequestingAuthorization && verificationCodeMethod === 'SMS'
              "
              :disabled="isRequestingAuthorization"
              :label="t('INBOX_MGMT.WHATSAPP_REQUEST_VERIFICATION_CODE_SMS')"
              @click="requestPhoneVerificationCode('SMS')"
            />
            <NextButton
              variant="outline"
              :is-loading="
                isRequestingAuthorization && verificationCodeMethod === 'VOICE'
              "
              :disabled="isRequestingAuthorization"
              :label="t('INBOX_MGMT.WHATSAPP_REQUEST_VERIFICATION_CODE_VOICE')"
              @click="requestPhoneVerificationCode('VOICE')"
            />
          </div>
        </div>

        <template v-else>
          <p v-if="phoneAlreadyVerified" class="mb-0 text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.WHATSAPP_VERIFICATION_ALREADY_COMPLETE') }}
          </p>
          <p v-else class="mb-0 text-sm text-n-slate-11">
            {{
              t('INBOX_MGMT.WHATSAPP_VERIFICATION_CODE_SENT_BY', {
                method:
                  verificationCodeMethod === 'VOICE'
                    ? t('INBOX_MGMT.WHATSAPP_METHOD_VOICE')
                    : t('INBOX_MGMT.WHATSAPP_METHOD_SMS'),
              })
            }}
          </p>
          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <NextInput
              v-if="!phoneAlreadyVerified"
              v-model="verificationCode"
              inputmode="numeric"
              maxlength="6"
              autocomplete="one-time-code"
              :label="t('INBOX_MGMT.WHATSAPP_VERIFICATION_CODE_LABEL')"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_VERIFICATION_CODE_PLACEHOLDER')
              "
            />
            <NextInput
              v-model="verificationPin"
              type="password"
              inputmode="numeric"
              maxlength="6"
              autocomplete="off"
              :label="t('INBOX_MGMT.WHATSAPP_VERIFICATION_PIN_LABEL')"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_VERIFICATION_PIN_PLACEHOLDER')
              "
            />
          </div>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.WHATSAPP_VERIFICATION_PIN_HELP') }}
          </p>
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap gap-2">
              <NextButton
                variant="link"
                size="sm"
                :is-loading="
                  isRequestingAuthorization && verificationCodeMethod === 'SMS'
                "
                :disabled="isRequestingAuthorization"
                :label="t('INBOX_MGMT.WHATSAPP_REQUEST_VERIFICATION_CODE_SMS')"
                @click="requestPhoneVerificationCode('SMS')"
              />
              <NextButton
                variant="link"
                size="sm"
                :is-loading="
                  isRequestingAuthorization &&
                  verificationCodeMethod === 'VOICE'
                "
                :disabled="isRequestingAuthorization"
                :label="
                  t('INBOX_MGMT.WHATSAPP_REQUEST_VERIFICATION_CODE_VOICE')
                "
                @click="requestPhoneVerificationCode('VOICE')"
              />
            </div>
            <NextButton
              :is-loading="isRequestingAuthorization"
              :disabled="
                isRequestingAuthorization ||
                !verificationCodeIsValid ||
                !verificationPinIsValid
              "
              :label="verificationActionLabel"
              @click="verifyPhoneRegistration"
            />
          </div>
        </template>
      </div>
    </Dialog>
  </div>
</template>
